/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from "dayjs";
import { ImportItemsConfigs as EMPLOYEE_CONFIG } from "pages/Employee/constants";
import { ImportItemsConfigs as LEAVE_TYPE_CONFIG } from "pages/PayrollSetting/LeaveType/constants";
import { employeeImportService } from "services";
import { checkRequireValue } from "utils";
import { DataItem } from "utils/xlsx";

import { ModuleType, ConfigFieldImport } from "./ImportButton.types";

export function convertExcelDateToJSDate(serial: number) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);

  const fractionalDay = serial - Math.floor(serial) + 0.0000001;

  let totalSeconds = Math.floor(86400 * fractionalDay);

  const seconds = totalSeconds % 60;

  totalSeconds -= seconds;

  const hours = Math.floor(totalSeconds / (60 * 60));
  const minutes = Math.floor(totalSeconds / 60) % 60;

  return new Date(
    dateInfo.getFullYear(),
    dateInfo.getMonth(),
    dateInfo.getDate(),
    hours,
    minutes,
    seconds
  );
}

export const getConfigs = (type: ModuleType): ConfigFieldImport[] => {
  switch (type) {
    case "employee":
      return EMPLOYEE_CONFIG;

    case "department":
      return [];

    case "leaveType":
      return LEAVE_TYPE_CONFIG;

    default:
      return [];
  }
};

export const getService = (type: ModuleType) => {
  if (type === "employee") {
    return employeeImportService;
  }
  if (type === "leaveType") {
    return () => {};
  }
  return () => {};
};

export const getFieldKey = (type: ModuleType) => {
  if (type === "employee") {
    return "employees";
  }
  if (type === "leaveType") {
    return "leaveTypes";
  }
  return "employees";
};

export const getDataExcel = (value: any): DataItem => {
  return {
    value: String(value || ""),
    dataType: "string",
  };
};

export const getHeaderExcel = (config: ConfigFieldImport[]): DataItem[] => {
  const header: DataItem[] = [];

  config.forEach((item) => {
    header.push(getDataExcel(item.text));
  });

  return header;
};

export const generateExcelTemplate = (
  config: ConfigFieldImport[]
): DataItem[][] => {
  const header: DataItem[] = [];
  const body: DataItem[] = [];

  config.forEach((item) => {
    header.push(getDataExcel(item.text));
    body.push(getDataExcel(item.defaultText));
  });

  return [header, body];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBodyExcel = (
  data: any[],
  config: ConfigFieldImport[]
): DataItem[][] => {
  const body: DataItem[][] = [getHeaderExcel(config)];

  data.forEach((item) => {
    const row: DataItem[] = [];
    config.forEach((configItem) => {
      row.push(getDataExcel(item[configItem.field]));
    });
    body.push(row);
  });

  return body;
};

type RecordItem = Record<string, string | number | boolean | string[] | null>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDataFromExcel = (
  dataExcel: any[],
  configs: ConfigFieldImport[]
) => {
  const result = [];
  const rowBaseConfig = Array(dataExcel?.length).fill(configs);

  for (let i = 1; i < rowBaseConfig.length; i++) {
    const currentRow: string[] = (dataExcel[i] as string[]) || ([] as string[]);
    const data: RecordItem = {
      invalidList: [] as string[],
    } as RecordItem;

    (rowBaseConfig[i] as ConfigFieldImport[])?.forEach(
      (_configs: ConfigFieldImport, index: number) => {
        const currentConfig = _configs;
        const cell = currentRow[index];

        if (currentConfig.regex && currentConfig.type !== "date") {
          if (
            cell &&
            !currentConfig.regex.test(cell) &&
            Array.isArray(data?.invalidList)
          ) {
            data?.invalidList?.push(currentConfig.field);
          }
          data[currentConfig.field] = cell || null;
        } else {
          if (currentConfig.type === "number") {
            data[currentConfig.field] = Number(cell ?? 0) || null;
          }
          if (currentConfig.type === "text") {
            data[currentConfig.field] = String(cell ?? "") || null;
          }
          if (currentConfig.type === "date") {
            if (typeof cell === "number") {
              const date = convertExcelDateToJSDate(cell);
              data[currentConfig.field] = dayjs(date).format("YYYY-MM-DD");
            } else if (!currentConfig?.regex?.test(cell)) {
              if (cell && Array.isArray(data?.invalidList)) {
                data?.invalidList?.push(currentConfig.field);
              }
              data[currentConfig.field] = cell || null;
            }
          }
          if (currentConfig.type === "boolean") {
            data[currentConfig.field] = cell === "true";
          }
        }
        if (
          currentConfig.required &&
          checkRequireValue(data[currentConfig.field])
        ) {
          if (Array.isArray(data?.invalidList)) {
            data?.invalidList?.push(currentConfig.field);
            data[currentConfig.field] = `${currentConfig.text} is required`;
          }
        }
      }
    );
    data.id = Number(i - 1 ?? 0);
    data.rowId = i;
    result.push(data);
  }
  return result;
};
